import type { ClientSummary } from '../schemas/client';

/**
 * How the list is narrowed.
 *
 * ## Why "Ready" is not "has an owner"
 *
 * The directory answers three ways: it has people, it has none, or it could not
 * be asked. `ready` means **not known to be missing one** — so a client whose
 * directory was unreachable stays in the default view rather than being
 * accused.
 *
 * That asymmetry is deliberate. Being wrong towards "fine" costs a row an
 * operator does not investigate; being wrong towards "broken" sends them to fix
 * something that works. Only the second wastes their time and their trust in
 * the screen.
 */
export const CLIENT_FILTERS = ['ready', 'needs-owner', 'all'] as const;
export type ClientFilter = (typeof CLIENT_FILTERS)[number];

export const FILTER_LABEL: Record<ClientFilter, string> = {
    ready: 'Ready',
    'needs-owner': 'Needs owner',
    all: 'All',
};

export const FILTER_HINT: Record<ClientFilter, string> = {
    ready: 'Not known to be missing an owner.',
    'needs-owner': 'The directory reports nobody who can sign in.',
    all: 'Everything in this environment.',
};

export function isClientFilter(value: string | null): value is ClientFilter {
    return value !== null && (CLIENT_FILTERS as readonly string[]).includes(value);
}

const matchesFilter = (client: ClientSummary, filter: ClientFilter): boolean => {
    if (filter === 'all') return true;
    // Both branches test `=== false` rather than truthiness, so an ABSENT
    // answer falls into `ready` and out of `needs-owner`. `!client.hasOwner`
    // would put "we could not ask" in the broken pile, which is the one
    // direction this must not be wrong in.
    if (filter === 'needs-owner') return client.hasOwner === false;
    return client.hasOwner !== false;
};

/**
 * Search over what is on the row.
 *
 * The name and the address, because those are what the operator can see and
 * therefore what they will type. Not the tenant id: it is on the detail screen,
 * and matching a hidden field makes a row appear for a reason the screen does
 * not show.
 */
const matchesSearch = (client: ClientSummary, search: string): boolean => {
    if (search.length === 0) return true;
    const needle = search.trim().toLowerCase();

    return (
        client.displayName.toLowerCase().includes(needle) ||
        (client.slug ?? '').toLowerCase().includes(needle)
    );
};

export function applyClientFilters(
    clients: readonly ClientSummary[],
    filter: ClientFilter,
    search: string,
): ClientSummary[] {
    return clients.filter(
        (client) => matchesFilter(client, filter) && matchesSearch(client, search),
    );
}

/** How many rows the current filter is hiding, so the screen can say so. */
export function countNeedingOwner(clients: readonly ClientSummary[]): number {
    return clients.filter((client) => client.hasOwner === false).length;
}
